import { useSiteMetadata } from 'hooks/useSiteMetadata';
import { isValidPath, getBlogUrlSlug } from 'helpers/url';

import {
  AppLink,
  BlogLink,
  CommunityLink,
  FlexibleLinkComponent,
  GatsbyLink,
  HelpLink,
  PartnerLink,
} from 'components/Links';

import { validateDomain, isUrlAndSameOrigin } from './validateDomain';

import type { ReactNode, FC } from 'react';
import type { Block, Inline } from '@contentful/rich-text-types';

export interface RichTextHyperlinkProps {
  to: string;
  children: ReactNode;
}

export const RichTextHyperlink: FC<RichTextHyperlinkProps> = ({ to, children }) => {
  const { appUrl, communityUrl, helpUrl, siteUrl } = useSiteMetadata();

  const receivedErrorText = `Received '${to}' for link '${children}'.`;
  const requireRelativeLinkError = new Error(
    `Contentful Rich Text links must be relative, starting with '/'. ${receivedErrorText}`
  );

  if (!to || typeof to !== 'string') {
    throw new Error(`Contentful Rich Text Hyperlink requires a link. ${receivedErrorText}`);
  }

  // "safe" External Links:
  if (isUrlAndSameOrigin(to, appUrl)) return <AppLink to={to}>{children}</AppLink>;
  if (isUrlAndSameOrigin(to, helpUrl)) return <HelpLink to={to}>{children}</HelpLink>;
  if (isUrlAndSameOrigin(to, communityUrl)) {
    return <CommunityLink to={to}>{children}</CommunityLink>;
  }

  // We prefer a relative link for our own same-site traffic.
  if (isUrlAndSameOrigin(to, siteUrl)) throw requireRelativeLinkError;

  if (to.startsWith('http') || to.includes('://')) {
    const isValid = validateDomain(to, receivedErrorText); // NOTE: This will throw an error if it fails.

    // NOTE: isValid should always be true or throw an error, this is just to protect against a `validateDomain` change.
    if (!isValid) {
      throw new Error(`Contentful Rich Text domain validation failed. ${receivedErrorText}`);
    }

    return (
      <FlexibleLinkComponent openInNewTab to={to}>
        {children}
      </FlexibleLinkComponent>
    );
  }

  // Broken scenario: a weird url, eg. `to="httpsharesight.com"`.  Shouldn't get to this state; Contentful validation should block it.
  if (!to.startsWith('/')) throw requireRelativeLinkError;

  if (!isValidPath(to)) {
    throw new Error(
      `Contentful Rich Text links must match a specific, eg. '/path/to/page-1'. ${receivedErrorText}`
    );
  }

  // Send Blog links off elsewhere.
  if (/^\/blog\/[A-z0-9]+/.test(to)) return <BlogLink to={getBlogUrlSlug(to)}>{children}</BlogLink>;

  // Send Partner links off elsewhere.
  if (/^\/partners\/[A-z0-9]+/.test(to)) return <PartnerLink to={to}>{children}</PartnerLink>;

  return <GatsbyLink to={to}>{children}</GatsbyLink>;
};

export const getHyperlinkComponent = (
  node: Block | Inline,
  children: RichTextHyperlinkProps['children']
) => <RichTextHyperlink to={node.data.uri as string}>{children}</RichTextHyperlink>;
